import { useUnreadLeads } from '@components/SidebarNew/hooks/useUnreadLeads'
import { leadsErrorCodes } from '@constants/leadsErrorCodes'
import { useAcl } from '@contexts/acl'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useLazyService } from '@hooks/useLazyService'
import { useLeadsContext } from '@pages/Leads/routes/Leads/contexts'
import { markAsRead } from '@services/leads/markAsRead'
import { Lead, LeadField } from '@services/leads/types'
import {
  Alert,
  Button,
  Loader,
  ModalBody,
  ModalHeader,
  Spacer,
  Spreader
} from '@ui-kit'
import { SideModalResponsiveWrapper } from '@ui-kit/Modal/SideModal'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'simple-flexbox'

import { MarkAsSpam } from '../Table/MarkAsSpam'
import styles from './DetailsModal.module.scss'
import { EditFields } from './EditFields'
import { BaseField, CreateDateField, SourceField } from './Fields'
import { WebsiteField } from './Fields/WebsiteField'

const renderField = (field: LeadField) => {
  const { subtype } = field
  const type = subtype || field.name

  const props = {
    label: field.label,
    value: field.value
  }

  switch (type) {
    case 'name':
    case 'email':
    case 'phone':
    case 'checkbox':
    case 'title':
    case 'password':
    case 'company':
    case 'address':
      return <BaseField {...props} key={type} />
    case 'number':
    case 'IP':
    case 'ip':
    case 'text':
    case 'radio':
    case 'select':
      return <BaseField {...props} withCopy={false} key={type} />
    case 'website':
    case 'file':
      return <WebsiteField {...props} />
    default:
      return <BaseField {...props} withCopy={false} key={type} />
  }
}

const ModalContent = ({ currentLead }: { currentLead: Lead }) => {
  const { attributes, id } = currentLead
  const {
    source_type,
    source_name,
    fields,
    is_spam,
    created_at_formatted,
    source_href
  } = attributes

  const { remove } = useModal()

  const [editMode, setEditMode] = useState(false)

  const exitEditMode = () => setEditMode(false)

  useEffect(() => {
    exitEditMode()
  }, [id])

  const { t, i18n } = useTranslation()

  const {
    refreshLeadsList,
    setSelectedLeads,
    nextLead,
    prevLead,
    isCurrentLeadFirst,
    isCurrentLeadLast,
    refreshCurrentLeadsList
  } = useLeadsContext()

  const { refreshUnreadLeadsList } = useUnreadLeads()

  const [markAsReadRequest] = useLazyService(markAsRead, {
    forwardError: true
  })

  const markLeadAsRead = useCallback(
    async (identifier: string) => {
      try {
        await markAsReadRequest({ identifiers: [identifier] })

        await refreshCurrentLeadsList(
          async currentData => {
            if (!currentData) return currentData

            const optimisticData = {
              ...currentData,
              collection: currentData.collection.map(currentLead => {
                if (currentLead.id === identifier) {
                  const readLead = {
                    ...currentLead,
                    attributes: { ...currentLead.attributes, is_read: true }
                  }

                  return readLead
                }

                return currentLead
              })
            }

            return optimisticData
          },
          { revalidate: true, rollbackOnError: true }
        )

        refreshUnreadLeadsList()
      } catch {
        refreshCurrentLeadsList()
      }
    },
    [markAsReadRequest, refreshCurrentLeadsList, refreshUnreadLeadsList]
  )

  useEffect(() => {
    if (!currentLead.attributes.is_read) {
      markLeadAsRead(currentLead.identifier)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLead])

  const hasAccessToMarkAsSpam = useAcl('leads.mark_as_spam')
  const hasAccessToEdit = useAcl('leads.edit')

  const errorCode = attributes.error_code || '0000'
  const lang = i18n.language as 'pl' | 'en' | 'pt'
  const langErrors = leadsErrorCodes[lang] || leadsErrorCodes.en
  const error = langErrors[errorCode as keyof typeof langErrors]

  return (
    <Fragment>
      <ModalHeader heading={t('word.lead-details')}>
        {hasAccessToMarkAsSpam && (
          <MarkAsSpam
            identifiers={[currentLead.identifier]}
            isSpam={is_spam}
            refreshLeadsList={refreshLeadsList}
            setSelectedLeads={setSelectedLeads}
            onSuccess={remove}
          />
        )}

        {hasAccessToEdit && (
          <Button
            variant='text-neutral'
            icon='icon-create'
            data-testid='edit-lead'
            onClick={() => setEditMode(prev => !prev)}
          />
        )}
      </ModalHeader>

      <Spacer space={10} />

      {attributes.error_code && (
        <ModalBody>
          <Alert variant='error'>{error}</Alert>
        </ModalBody>
      )}

      {editMode ? (
        <EditFields lead={currentLead} exitEditMode={exitEditMode} />
      ) : (
        <div className={styles.detailsWrapper}>
          <ModalBody className={styles.fields}>
            <SourceField
              source_name={source_name}
              source_type={source_type}
              source_href={source_href}
            />
            {fields.map(field => renderField(field))}
            <CreateDateField date={created_at_formatted} />
          </ModalBody>
          <ModalBody className={styles.footer}>
            <Row>
              <Button
                variant='text-primary'
                onClick={prevLead}
                disabled={isCurrentLeadFirst}
                icon='icon-arrow-left'
              />

              <Spreader spread={5} />

              <Button
                variant='text-primary'
                onClick={nextLead}
                disabled={isCurrentLeadLast}
                icon='icon-arrow-right'
              />
            </Row>
          </ModalBody>
        </div>
      )}
    </Fragment>
  )
}

export const ModalDetails = NiceModal.create(() => {
  const { getCurrentLead, isLeadsListLoading } = useLeadsContext()
  const { remove } = useModal()
  const { t } = useTranslation()

  const currentLead = getCurrentLead(remove)

  if (isLeadsListLoading) {
    return (
      <SideModalResponsiveWrapper
        onClose={remove}
        surface={2}
        size='fullscreen'
        outsideClose={{
          idSubstringToIgnore: 'table-row',
          parentIdToStopAt: 'leads-table-wrapper'
        }}
      >
        <ModalHeader heading={t('word.lead-details')} />
        <ModalBody>
          <Loader />
        </ModalBody>
      </SideModalResponsiveWrapper>
    )
  }

  if (!currentLead) {
    return null
  }

  return (
    <SideModalResponsiveWrapper
      onClose={remove}
      surface={2}
      className={styles.modal}
      size='fullscreen'
      outsideClose={{
        idSubstringToIgnore: 'table-row',
        parentIdToStopAt: 'leads-table-wrapper'
      }}
    >
      <ModalContent currentLead={currentLead} />
    </SideModalResponsiveWrapper>
  )
})

ModalDetails.displayName = 'ModalDetails'
